import { FC, useCallback, useEffect, useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import {
  Grid,
  GridColumn,
  GridToolbar,
  GridCellProps,
  GridItemChangeEvent
} from '@progress/kendo-react-grid'
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'
import {
  Checkbox,
  CheckboxChangeEvent,
  Input,
  InputChangeEvent,
  NumericTextBox,
  NumericTextBoxChangeEvent,
  Switch,
  SwitchChangeEvent
} from '@progress/kendo-react-inputs'

import { DropDownCommandCell } from './DropDownCommandCell'
import {
  BranchSelect,
  ConfirmDialog,
  SupplierSelect,
  SupplierSelectProps,
  ErrorMessage
} from '../../../components/parts'
import './Supplier.css'
import {
  GetExpenseListItem,
  DeleteSupplierItemReqData,
  GetFactoryListItem,
  GetPersonListItem,
  GetSupplierItemBaseInfoObject,
  SearchResult
} from '@gateway.maintenance'
import produce from 'immer'
import {
  SupplierSearchActions,
  CommonActions,
  useAppDispatch,
  useAppSelector
} from '../../../stores'
import { SupplierItemGateway } from '../../../gateway/maintenance/SupplierItemGateway'
import { SupplierListGateway } from '../../../gateway/maintenance/SupplierListGateway'
import { MessageProvider } from '../../../services'
import { Authority } from '../../../gateway/system/Authority'

export interface SupplierInputProps {
  searchResult?: SearchResult
  setShowMaintenanceDialog: React.Dispatch<React.SetStateAction<boolean>>
  setNotificationMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessage[]>>
  skip: number
  setSkip: React.Dispatch<React.SetStateAction<number>>
  isFixMode: boolean
  isAddSupplierBranchMode: boolean
}
const initBaseInfoObject = {
  supplierCd: '',
  supplierBranchCd: '',
  supplierBranchNm: null,
  addFlg: 0,
  supplierNm: '',
  branchCd: '',
  buildingDiscloseFlg: 0,
  ourCompanyCapacityFlg: 0,
  displayOrder: null,
  updDtm: '',
  ndaFlg: 0
}
export const SupplierInput: FC<SupplierInputProps> = ({ searchResult, setShowMaintenanceDialog, setNotificationMessages, setErrorMessages, skip, setSkip, isFixMode, isAddSupplierBranchMode }) => {
  const [baseInfo, setBaseInfo] =
    useState<GetSupplierItemBaseInfoObject>(initBaseInfoObject)
  const [baseInfoBefore, setBaseInfoBefore] = useState<GetSupplierItemBaseInfoObject>(initBaseInfoObject)
  // 費目
  const [expenseList, setExpenseList] = useState<GetExpenseListItem[]>([])
  const [expenseListBefore, setExpenseListBefore] = useState<GetExpenseListItem[]>([])
  // 工場
  const [factoryList, setFactoryList] = useState<GetFactoryListItem[]>([])
  const [factoryListBefore, setFactoryListBefore] = useState<GetFactoryListItem[]>([])
  // 担当者
  const [personList, setPersonList] = useState<GetPersonListItem[]>([])
  const [personListBefore, setPersonListBefore] = useState<GetPersonListItem[]>([])

  const [isSearchBtnDisabled, setIsSearchBtnDisabled] = useState(true)

  // 業者の新規登録か更新かを管理するstate
  // trueの時は新規登録
  const [isNewOccasion, setIsNewOccasion] = useState(false)
  const [isLoginUpdataDisabled, setIsLoginUpdataDisabled] = useState(false)

  const [confirmDialogGoBackShow, setConfirmDialogGoBackShow] = useState(false)
  const [confirmDialogSignInShow, setConfirmDialogSignInShow] = useState(false)
  const [confirmDialogDetailDeleteShow, setConfirmDialogDetailDeleteShow] = useState(false)
  const userProp = useAppSelector((state) => state.common.userProp)
  const common = useAppSelector((x) => x.common)
  const codeBranchNames = common.initialData.codeNames('SI01')
  const codeExpenseNames = common.initialData.codeNames('HI01')
  const dispatch = useAppDispatch()
  const searchCondation = useAppSelector(
    (state) => state.supplierSearch.searchCondation
  )

  // 支店ユーザかつ自支店以外の判定フラグ
  // const isNotOwnBranch = (userProp?.authority === Authority.Branch && userProp?.branchCode !== baseInfo.branchCd)

  // 工務権限以外の判定フラグ
  const isNotBuilderAuthority = userProp?.authority !== Authority.Builder

  /**
   * 基本情報オブジェクト比較
   */
  const baseInfoObjCompare = (obj1: GetSupplierItemBaseInfoObject, obj2: GetSupplierItemBaseInfoObject) => {
    const obj1Keys = Object.keys(obj1) as Array<keyof GetSupplierItemBaseInfoObject>
    const obj2Keys = Object.keys(obj2) as Array<keyof GetSupplierItemBaseInfoObject>
    if (obj1Keys.length !== obj2Keys.length) {
      return false
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    for (const k of obj1Keys as Array<keyof GetSupplierItemBaseInfoObject>) {
      if (obj1[k] !== obj2[k]) {
        return false
      }
    }
    return true
  }

  /**
   * 費目情報オブジェクト比較
   */
  const expenseObjCompare = (obj1: GetExpenseListItem[], obj2: GetExpenseListItem[]) => {
    if (obj1.length !== obj2.length) {
      return false
    }

    for (const [i, v] of Object.entries(obj1)) {
      const obj1Keys = Object.keys(v) as Array<keyof GetExpenseListItem>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      for (const k of obj1Keys as Array<keyof GetExpenseListItem>) {
        if (obj1[Number(i)][k] !== obj2[Number(i)][k]) {
          return false
        }
      }
    }

    return true
  }

  /**
   * 工場情報オブジェクト比較
   */
  const factoryObjCompare = (obj1: GetFactoryListItem[], obj2: GetFactoryListItem[]) => {
    if (obj1.length !== obj2.length) {
      return false
    }

    for (const [i, v] of Object.entries(obj1)) {
      const obj1Keys = Object.keys(v) as Array<keyof GetFactoryListItem>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      for (const k of obj1Keys as Array<keyof GetFactoryListItem>) {
        if (obj1[Number(i)][k] !== obj2[Number(i)][k]) {
          return false
        }
      }
    }

    return true
  }

  /**
   * 担当者情報オブジェクト比較
   */
  const personObjCompare = (obj1: GetPersonListItem[], obj2: GetPersonListItem[]) => {
    if (obj1.length !== obj2.length) {
      return false
    }

    for (const [i, v] of Object.entries(obj1)) {
      const obj1Keys = Object.keys(v) as Array<keyof GetPersonListItem>
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      for (const k of obj1Keys as Array<keyof GetPersonListItem>) {
        if (obj1[Number(i)][k] !== obj2[Number(i)][k]) {
          return false
        }
      }
    }

    return true
  }

  /**
   * 費目 dataChange
   */
  const handleDataExpense = (e: GridItemChangeEvent) => {
    const field = e.field ?? ''
    const changeData = expenseList.map((item: GetExpenseListItem) =>
      item === e.dataItem ? { ...item, [field]: e.value } : item
    )

    setExpenseList(changeData)
  }
  /**
   * 工場 dataChange
   */
  const handleDataFactory = (e: GridItemChangeEvent) => {
    const field = e.field ?? ''
    // 配分比率  checkVal
    if (field === 'allocationRatio') {
      if (isNaN(Number(e.value))) {
        return
      }
    }
    const changeData = factoryList.map((item: GetFactoryListItem) =>
      item === e.dataItem ? { ...item, [field]: e.value } : item
    )
    setFactoryList(changeData)
  }
  /**
   * 担当者 dataChange
   */
  const handlePerson = (e: GridItemChangeEvent) => {
    const field = e.field ?? ''
    const changeData = personList.map((item: GetPersonListItem) =>
      item === e.dataItem ? { ...item, [field]: e.value } : item
    )
    setPersonList(changeData)
  }
  /**
   * 費目 行追加
   */
  const addNewExpenseItem = () => {
    const initExpense = {
      inEdit: true,
      isDel: false,
      newAddFlg: true,
      supplierCd: '',
      expense: '',
      soyukaiFlg: 0,
      hearingSheetFlg: 0,
      replyReqFlg: 0,
      updDtm: '',
      beforeExpense: ''
    }
    setExpenseList([...expenseList, initExpense])
  }
  /**
   * 工場  行追加
   */
  const addNewFactoryItem = () => {
    const initFactoryItem = {
      inEdit: true,
      isDel: false,
      newAddFlg: true,
      supplierCd: '',
      expense: '',
      factoryCd: '',
      factoryNm: '',
      allocationRatio: 0,
      updDtm: '',
      beforeFactoryNm: ''
    }
    setFactoryList([...factoryList, initFactoryItem])
  }
  /**
   * 担当者 行追加
   */
  const addNewPersonItem = () => {
    const initPersonItem = {
      inEdit: true,
      isDel: false,
      newAddFlg: true,
      supplierCd: '',
      supplierSection: '',
      supplierPerson: '',
      affiliationContacts: '',
      mailAddress: '',
      mainPersonFlg: 0,
      updDtm: '',
      beforeMailAddress: ''
    }
    setPersonList([...personList, initPersonItem])
  }
  /**
   * 業者コードと業者名の取得
   */
  const handleSupplierSearchListItem: SupplierSelectProps['supplierSearchListItem'] =
    (supplierItem) => {
      setBaseInfo(
        produce((draft) => {
          draft.supplierCd = baseInfo.addFlg === 1 ? draft.supplierCd : supplierItem.supplierCd
          draft.supplierNm = supplierItem.supplierNm
        })
      )
    }
  /**
   * 入場可能人数フラグ
   */
  const onSwitchOurCompanyCapacityFlg = (event: SwitchChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.ourCompanyCapacityFlg = event.value ? 1 : 0
      })
    )
  }
  /**
   * 物件情報開示フラグ
   */
  const onSwitchBuildingDiscloseFlg = (event: SwitchChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.buildingDiscloseFlg = Number(event.value)
      })
    )
  }
  /**
   * 秘密保持契約フラグ
   */
  const handleNdaFlg = (event: SwitchChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.ndaFlg = event.value ? 1 : 0
        // 秘密保持契約フラグ変更時は物件情報開示フラグも同じ値に連動させる
        draft.buildingDiscloseFlg = event.value ? 1 : 0
      })
    )
  }
  /**
   * 業者支店名
   */
  const handleSupplierBranchNm = (event: InputChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.supplierBranchNm = event.value
      })
    )
  }
  /**
   * 表示順
   */
  const handleDisplayOrderVal = (event: NumericTextBoxChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.displayOrder = Number(event.value)
      })
    )
  }
  /**
   * 独自追加フラグ
   */
  const HandleAddFlgVal = (event: CheckboxChangeEvent) => {
    setBaseInfo(
      produce((draft) => {
        draft.addFlg = event.value ? 1 : 0
      })
    )
  }
  /**
   * 支店コード
   */
  const handleBranchSelect = (branchNm: string[]) => {
    setBaseInfo(
      produce((draft) => {
        draft.branchCd = branchNm[0]
      })
    )
  }
  /**
   * 登録/修正
   */
  const handleLoginAmendment = () => {
    setConfirmDialogSignInShow(true)
  }
  /**
   * 明細削除
   */
  const handleDetailDelete = () => {
    setConfirmDialogDetailDeleteShow(true)
  }
  /**
   * 修正 登録 dialogConfirm
   */
  const dialogConfirmSignInBtn = () => {
    /**
     * 登録・更新データの抽出
     * expenseList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesExpenseList: GetExpenseListItem[] = []
    expenseList.forEach((item: GetExpenseListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesExpenseList.push(item)
      }
    })
    /**
     * 登録・更新データの抽出
     * factoryList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesFactoryList: GetFactoryListItem[] = []
    factoryList.forEach((item: GetFactoryListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesFactoryList.push(item)
      }
    })
    /**
     * 登録・更新データの抽出
     * personList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesPersonList: GetPersonListItem[] = []
    personList.forEach((item: GetPersonListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesPersonList.push(item)
      }
    })
    /**
     * 登録・更新データを作成する
     * expenseList
     */
    const filterLoginUpdatesExpenseList =
      complianceLoginUpdatesExpenseList.filter((item) => item.isDel === false)
    /**
     * 登録・更新データを作成する
     * factoryList
     */
    const filterLoginUpdatesFactoryList =
      complianceLoginUpdatesFactoryList.filter((item) => item.isDel === false)
    /**
     * 登録・更新データを作成する
     * personList
     */
    const filterLoginUpdatesPersonList =
      complianceLoginUpdatesPersonList.filter((item) => item.isDel === false)

    const expenseListResult = filterLoginUpdatesExpenseList.map((item) => {
      const params = {
        expense: item.expense,
        soyukaiFlg: Number(item.soyukaiFlg),
        hearingSheetFlg: Number(item.hearingSheetFlg),
        replyReqFlg: Number(item.replyReqFlg),
        updDtm: item.updDtm,
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        beforeExpense: item.newAddFlg ? item.expense : item.beforeExpense
      }
      return params
    })
    const factoryListResult = filterLoginUpdatesFactoryList.map((item) => {
      const params = {
        expense: item.expense,
        factoryCd: item.factoryCd,
        factoryNm: item.factoryNm,
        allocationRatio: Number(item.allocationRatio),
        updDtm: item.updDtm,
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        beforeFactoryNm: item.newAddFlg ? item.factoryNm : item.beforeFactoryNm
      }
      return params
    })
    const personListResult = filterLoginUpdatesPersonList.map((item) => {
      const params = {
        supplierSection: item.supplierSection,
        supplierPerson: item.supplierPerson,
        affiliationContacts: item.affiliationContacts,
        mailAddress: item.mailAddress,
        mainPersonFlg: Number(item.mainPersonFlg),
        updDtm: item.updDtm,
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        beforeMailAddress: item.newAddFlg ? item.mailAddress : item.beforeMailAddress
      }
      return params
    })
    const baseInfoFix = {
      ...baseInfo,
      supplierNm: baseInfo.addFlg === 1 ? baseInfo.supplierNm : null,
      updDtm: baseInfo.updDtm !== '' ? baseInfo.updDtm : null
    }
    // 業者の新規登録または業者の支店追加
    if (isNewOccasion || isAddSupplierBranchMode) {
      const expenseListFix = expenseListResult.map(expense => {
        return {
          ...expense,
          soyukaiFlg: Number(expense.soyukaiFlg),
          hearingSheetFlg: Number(expense.hearingSheetFlg),
          replyReqFlg: Number(expense.replyReqFlg),
          updDtm: expense.updDtm === '' ? null : expense.updDtm
        }
      })
      const factoryListFix = factoryListResult.map(factory => {
        return {
          ...factory,
          allocationRatio: Number(factory.allocationRatio),
          updDtm: factory.updDtm === '' ? null : factory.updDtm
        }
      })
      const personListFix = personListResult.map(person => {
        return {
          ...person,
          mainPersonFlg: Number(person.mainPersonFlg),
          updDtm: person.updDtm === '' ? null : person.updDtm
        }
      })
      dispatch(CommonActions.setIsLoading(true))
      SupplierItemGateway.PostSupplieInputItem({
        data: {
          baseInfo: baseInfoFix,
          expenseList: expenseListFix,
          factoryList: factoryListFix,
          personList: personListFix
        }
      })
        .then((res) => {
          if (!res.error) {
            SupplierListGateway.fetchSupplierSearchList(searchCondation)
              .then((response) => {
                if (!response.error) {
                  dispatch(
                    SupplierSearchActions.setSearchResult(response.data.infoList)
                  )
                  setSkip(skip)
                } else {
                  if (Array.isArray(res.messages) && res.messages.length > 0) {
                    const errorMessages = res.messages.map<ErrorMessage>((message) => {
                      return {
                        id: message.id,
                        content: message.message
                      }
                    })
                    setErrorMessages(errorMessages)
                  }
                }
              })
              .finally(() => {})

            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', isFixMode ? '修正' : '登録'
              )
            }]
            setNotificationMessages(message)
            setShowMaintenanceDialog(false)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => { dispatch(CommonActions.setIsLoading(false)) })
    } else {
      // 修正
      const expenseListFix = expenseListResult.map(expense => {
        return { ...expense, updDtm: expense.updDtm === '' ? null : expense.updDtm }
      })
      const factoryListFix = factoryListResult.map(factory => {
        return { ...factory, updDtm: factory.updDtm === '' ? null : factory.updDtm }
      })
      const personListFix = personListResult.map(person => {
        return { ...person, updDtm: person.updDtm === '' ? null : person.updDtm }
      })
      dispatch(CommonActions.setIsLoading(true))
      SupplierItemGateway.PutSupplieInputItem({
        data: {
          baseInfo: baseInfoFix,
          expenseList: expenseListFix,
          factoryList: factoryListFix,
          personList: personListFix
        }
      })
        .then((res) => {
          if (!res.error) {
            SupplierListGateway.fetchSupplierSearchList(searchCondation)
              .then((response) => {
                if (!response.error) {
                  dispatch(
                    SupplierSearchActions.setSearchResult(response.data.infoList)
                  )
                  setSkip(skip)
                } else {
                  if (Array.isArray(res.messages) && res.messages.length > 0) {
                    const errorMessages = res.messages.map<ErrorMessage>((message) => {
                      return {
                        id: message.id,
                        content: message.message
                      }
                    })
                    setErrorMessages(errorMessages)
                  }
                }
              })
              .finally(() => {})

            const message: ErrorMessage[] = [{
              id: 'I004',
              content: MessageProvider.provide(
                'I004', isFixMode ? '修正' : '登録'
              )
            }]
            setNotificationMessages(message)
            setShowMaintenanceDialog(false)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => { dispatch(CommonActions.setIsLoading(false)) })
    }
    setConfirmDialogSignInShow(false)
  }
  /**
   * 明細削除 dialogConfirm
   */
  const dialogConfirmDetailDeleteBtn = () => {
    /**
     * 登録・更新データの抽出
     * expenseList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesExpenseList: GetExpenseListItem[] = []
    expenseList.forEach((item: GetExpenseListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesExpenseList.push(item)
      }
    })
    /**
     * 登録・更新データの抽出
     * factoryList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesFactoryList: GetFactoryListItem[] = []
    factoryList.forEach((item: GetFactoryListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesFactoryList.push(item)
      }
    })
    /**
     * 登録・更新データの抽出
     * personList 登録・更新条件と一致するデータ
     */
    const complianceLoginUpdatesPersonList: GetPersonListItem[] = []
    personList.forEach((item: GetPersonListItem) => {
      if (
        !(item.isDel === true && (item.updDtm === '' || item.updDtm === null))
      ) {
        complianceLoginUpdatesPersonList.push(item)
      }
    })
    /**
     * 削除データを抽出
     */
    if ((baseInfo.updDtm ?? '') !== '') {
      /**
       * ExpenseList 削除データを抽出
       */
      const expenseList = complianceLoginUpdatesExpenseList.filter(
        (item) => item?.isDel === true && item.newAddFlg === false
      )
      /**
       * FactoryList 削除データを抽出
       */
      const factoryList = complianceLoginUpdatesFactoryList.filter(
        (item) => item?.isDel === true && item.newAddFlg === false
      )
      /**
       * PersonList 削除データを抽出
       */
      const personList = complianceLoginUpdatesPersonList.filter(
        (item) => item?.isDel === true && item.newAddFlg === false
      )
      if (
        expenseList.length > 0 ||
        factoryList.length > 0 ||
        personList.length > 0
      ) {
        /**
         * 業者マスタメンテナンス-登録・更新
         * 明細削除
         */
        const delExpenseList = expenseList.map((item) => {
          const delExpenseListItem = {
            expenseCd: item.beforeExpense,
            expenseNm:
                codeExpenseNames.find((x) => x.CODE === item.expense)?.NAME ?? '',
            updDtm: item.updDtm
          }
          return delExpenseListItem
        })
        const delFactoryList = factoryList.map((item) => {
          const delFactoryListItem = {
            expense: item.expense,
            factoryCd: item.factoryCd,
            factoryNm: item.factoryNm,
            updDtm: item.updDtm
          }
          return delFactoryListItem
        })
        const delPersonList = personList.map((item) => {
          const delPersonListItem = {
            mailAddress: item.beforeMailAddress,
            updDtm: item.updDtm
          }
          return delPersonListItem
        })
        const params: DeleteSupplierItemReqData = {
          data: {
            baseInfo: {
              supplierCd: baseInfo.supplierCd,
              supplierBranchCd: baseInfo.supplierBranchCd
            },
            expenseList: delExpenseList,
            factoryList: delFactoryList,
            personList: delPersonList
          }
        }

        dispatch(CommonActions.setIsLoading(true))
        SupplierItemGateway.DeleteSupplierItem(params)
          .then((res) => {
            if (!res.error) {
              SupplierListGateway.fetchSupplierSearchList(searchCondation)
                .then((response) => {
                  if (!response.error) {
                    dispatch(
                      SupplierSearchActions.setSearchResult(response.data.infoList)
                    )
                    setSkip(skip)
                  } else {
                    if (Array.isArray(res.messages) && res.messages.length > 0) {
                      const errorMessages = res.messages.map<ErrorMessage>((message) => {
                        return {
                          id: message.id,
                          content: message.message
                        }
                      })
                      setErrorMessages(errorMessages)
                    }
                  }
                })
                .finally(() => {})

              const message: ErrorMessage[] = [{
                id: 'I004',
                content: MessageProvider.provide(
                  'I004', '明細削除'
                )
              }]
              setNotificationMessages(message)
              setShowMaintenanceDialog(false)
            } else {
              if (Array.isArray(res.messages) && res.messages.length > 0) {
                const errorMessages = res.messages.map<ErrorMessage>((message) => {
                  return {
                    id: message.id,
                    content: message.message
                  }
                })
                setErrorMessages(errorMessages)
              }
            }
          })
          .finally(() => { dispatch(CommonActions.setIsLoading(false)) })
      } else {
        const message: ErrorMessage[] = [{
          id: 'I004',
          content: MessageProvider.provide(
            'I004', '明細削除'
          )
        }]
        setNotificationMessages(message)
        setShowMaintenanceDialog(false)
      }
    }
    setConfirmDialogDetailDeleteShow(false)
  }

  /**
   * 費目エリアの削除チェックボックス
   */
  const onChangeExpenseDelFlg = (selectedRowIndex: number, selected: boolean) => {
    setExpenseList(
      produce((draft) => {
        draft[selectedRowIndex].isDel = !selected
      })
    )
  }

  /**
   * 費目エリアの倉友会チェックボックス
   */
  const onChangeSoyukaiFlg = (selectedRowIndex: number, selected: boolean) => {
    setExpenseList(
      produce((draft) => {
        draft[selectedRowIndex].soyukaiFlg = !selected ? 1 : 0
      })
    )
  }

  /**
   * 費目エリアのヒアリングシートチェックボックス
   */
  const onChangeHearingSheetFlg = (selectedRowIndex: number, selected: boolean) => {
    setExpenseList(
      produce((draft) => {
        draft[selectedRowIndex].hearingSheetFlg = !selected ? 1 : 0
      })
    )
  }

  /**
   * 費目エリアの回答依頼チェックボックス
   */
  const onChangeReplyReqFlg = (selectedRowIndex: number, selected: boolean) => {
    setExpenseList(
      produce((draft) => {
        draft[selectedRowIndex].replyReqFlg = !selected ? 1 : 0
      })
    )
  }

  /**
   * 工場エリアの削除チェックボックス
   */
  const onChangeFactoryDelFlg = (selectedRowIndex: number, selected: boolean) => {
    setFactoryList(
      produce((draft) => {
        draft[selectedRowIndex].isDel = !selected
      })
    )
  }

  /**
   * 担当者エリアの削除チェックボックス
   */
  const onChangePersonDelFlg = (selectedRowIndex: number, selected: boolean) => {
    setPersonList(
      produce((draft) => {
        draft[selectedRowIndex].isDel = !selected
        // 削除フラグが変更された場合は同一行の主担当フラグをOFFにする
        if (!selected) {
          draft[selectedRowIndex].mainPersonFlg = 0
        }
      })
    )
  }

  /**
   * 担当者エリアの主担当チェックボックス
   */
  const onChangeMainPersonFlg = (selectedRowIndex: number, selected: boolean) => {
    setPersonList(
      produce((draft) => {
        draft[selectedRowIndex].mainPersonFlg = !selected ? 1 : 0
        // 主担当フラグが変更された場合は同一行以外の行の主担当フラグをOFFにする
        if (!selected) {
          draft.forEach((v, i) => {
            if (i !== selectedRowIndex) {
              draft[i].mainPersonFlg = 0
            }
          })
        }
      })
    )
  }

  /**
   * 戻る ConfirmDialog
   */
  const dialogConfirmGoBackBtn = () => {
    setConfirmDialogGoBackShow(false)
    setShowMaintenanceDialog(false)
  }

  /**
   * 戻る Dialog
   */
  const goBack = () => {
    const diffBaseInfo = baseInfoObjCompare(baseInfo, baseInfoBefore)
    const diffExpense = expenseObjCompare(expenseList, expenseListBefore)
    const diffFactory = factoryObjCompare(factoryList, factoryListBefore)
    const diffPerson = personObjCompare(personList, personListBefore)
    if (!diffBaseInfo || !diffExpense || !diffFactory || !diffPerson) {
      setConfirmDialogGoBackShow(true)
    } else {
      setShowMaintenanceDialog(false)
    }
  }
  /**
   * 登録・更新ボタンは活性あるいは非活性を判断する
   */
  const loginUpdataDisabledChecked = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const isMainPersonChecked = personList.find((item) => item.mainPersonFlg && item.isDel === false)
    if (isMainPersonChecked === undefined) {
      setIsLoginUpdataDisabled(true)
      return
    }
    setIsLoginUpdataDisabled(false)
  }, [personList])

  useEffect(() => {
    loginUpdataDisabledChecked()
  }, [loginUpdataDisabledChecked])

  useEffect(() => {
    // 新規追加か更新か支店追加を判断する
    setIsNewOccasion((searchResult?.supplierCd ?? '') === '')

    if (searchResult !== undefined && (searchResult?.supplierCd ?? '') !== '' && searchResult?.supplierBranchCd !== '') {
      // 更新の場合
      const initBaseInfo = {
        supplierCd: searchResult.supplierCd,
        supplierBranchCd: searchResult.supplierBranchCd,
        supplierBranchNm: searchResult.supplierBranchNm,
        addFlg: 0,
        supplierNm: searchResult.supplierNm,
        branchCd:
        codeBranchNames.find((x) => x.NAME === searchResult?.branchNm)
          ?.CODE ?? '',
        buildingDiscloseFlg: 0,
        ourCompanyCapacityFlg: 0,
        displayOrder: searchResult.displayOrder,
        updDtm: '',
        ndaFlg: 0
      }
      setBaseInfo(initBaseInfo)
      setExpenseList([
        {
          inEdit: true,
          isDel: false,
          newAddFlg: true,
          supplierCd: '',
          beforeExpense: '',
          expense: '',
          soyukaiFlg: 0,
          hearingSheetFlg: 0,
          replyReqFlg: 0,
          updDtm: ''
        }
      ])
      /**
       * 業者マスタメンテナンス-詳細取得
       */
      dispatch(CommonActions.setIsLoading(true))
      SupplierListGateway.GetSupplierItem(searchResult?.supplierCd, searchResult?.supplierBranchCd)
        .then((res) => {
          if (!res.error) {
            const createExpenseList = res.data.expenseList.map(
              (item: GetExpenseListItem) => {
                item.inEdit = true
                item.isDel = false
                item.newAddFlg = false
                item.beforeExpense = item.expense
                return item
              }
            )
            const createFactoryList = res.data.factoryList.map(
              (item: GetFactoryListItem) => {
                item.inEdit = true
                item.isDel = false
                item.newAddFlg = false
                item.beforeFactoryNm = item.factoryNm
                return item
              }
            )

            const createPersonList = res.data.personList.map(
              (item: GetPersonListItem) => {
                item.inEdit = true
                item.isDel = false
                item.newAddFlg = false
                item.beforeMailAddress = item.mailAddress
                return item
              }
            )
            setBaseInfo(res.data.baseInfo)
            setBaseInfoBefore(res.data.baseInfo)
            // 費目
            setExpenseList(createExpenseList)
            setExpenseListBefore(createExpenseList)
            // 工場
            setFactoryList(createFactoryList)
            setFactoryListBefore(createFactoryList)
            // 担当者
            setPersonList(createPersonList)
            setPersonListBefore(createPersonList)
          } else {
            if (Array.isArray(res.messages) && res.messages.length > 0) {
              const errorMessages = res.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => { dispatch(CommonActions.setIsLoading(false)) })
    } else if (searchResult !== undefined && searchResult?.supplierCd !== '' && searchResult?.supplierBranchCd === '') {
      // 支店追加の場合
      const initBaseInfo = {
        supplierCd: searchResult?.supplierCd,
        supplierBranchCd: '',
        supplierBranchNm: null,
        addFlg: searchResult?.addFlg,
        supplierNm: searchResult?.supplierNm,
        branchCd: codeBranchNames.find((x) => x.NAME === searchResult?.branchNm)?.CODE ?? '',
        buildingDiscloseFlg: searchResult?.buildingDiscloseFlg,
        ourCompanyCapacityFlg: 0,
        displayOrder: null,
        updDtm: '',
        ndaFlg: searchResult?.ndaFlg
      }
      setBaseInfo(initBaseInfo)
      setExpenseList([])
      // 工場
      setFactoryList([])
      // 担当者
      setPersonList([])
    } else {
      // 新規追加の場合
      const initBaseInfo = {
        supplierCd: '',
        supplierBranchCd: '',
        supplierBranchNm: null,
        addFlg: 0,
        supplierNm: '',
        branchCd: '',
        buildingDiscloseFlg: 0,
        ourCompanyCapacityFlg: 0,
        displayOrder: null,
        updDtm: '',
        ndaFlg: 0
      }
      setBaseInfo(initBaseInfo)
      setExpenseList([])
      // 工場
      setFactoryList([])
      // 担当者
      setPersonList([])
    }
  }, [])

  useEffect(() => {
    /**
     * M1参照検索 disabled
     */
    if (
      (searchResult?.supplierCd === undefined ||
        searchResult?.supplierCd === '') &&
      baseInfo.addFlg === 0
    ) {
      setIsSearchBtnDisabled(false)
    } else {
      setIsSearchBtnDisabled(true)
    }
  }, [baseInfo.addFlg, searchResult?.supplierCd])

  return (
    <>
      <Card style={{ margin: 5 }}>
        <CardHeader className="trader-card-header">
          <CardTitle>業者情報</CardTitle>
          <div style={{ textAlign: 'right', fontWeight: 'bold' }}>
            <Checkbox
              style={{ marginRight: '10px' }}
              label="独自追加"
              onChange={HandleAddFlgVal}
              checked={baseInfo.addFlg === 1}
              disabled={!isNewOccasion || (isFixMode && isNotBuilderAuthority)}
            />
          </div>
        </CardHeader>

        <CardBody>
          <GridLayout
            rows={[{ height: 50 }, { height: 50 }]}
            cols={[{ width: 100 }, { width: 150 }, { width: 100 }, { width: 150 }, { width: 100 }, { width: 150 }, { width: 100 }, { width: 100 }]}
            gap={{ rows: 5, cols: 20 }}
            align={{ horizontal: 'stretch', vertical: 'middle' }}
          >
            <GridLayoutItem col={1} row={1}>
              業者コード
            </GridLayoutItem>
            <GridLayoutItem col={2} row={1}>
              <Input value={baseInfo.supplierCd} disabled />
            </GridLayoutItem>

            <GridLayoutItem col={1} row={2}>
              業者名
            </GridLayoutItem>
            <GridLayoutItem col={2} row={2} style={{ display: 'flex' }}>
              <SupplierSelect
                supplierSearchListItem={handleSupplierSearchListItem}
                isSearchBtnDisabled={isSearchBtnDisabled || (isFixMode && isNotBuilderAuthority)}
                isInputDisabled={baseInfo.addFlg === 0 || (isFixMode && isNotBuilderAuthority) || isAddSupplierBranchMode}
                searchFromXgrab
                supplierNm={searchResult?.supplierNm}
              />
            </GridLayoutItem>

            <GridLayoutItem col={3} row={1}>
              業者支店名
            </GridLayoutItem>
            <GridLayoutItem col={4} row={1}>
              <Input
                value={baseInfo.supplierBranchNm ?? ''}
                onChange={(e) => handleSupplierBranchNm(e)}
                disabled={isNotBuilderAuthority}
              />
            </GridLayoutItem>

            <GridLayoutItem col={5} row={1}>
              管轄支店
            </GridLayoutItem>
            <GridLayoutItem col={6} row={1}>
              <BranchSelect
                value={[baseInfo.branchCd]}
                onChange={handleBranchSelect}
                popupSettings={{ className: 'pcy-dropdownlist' }}
                disabled={isFixMode && isNotBuilderAuthority}
              />
            </GridLayoutItem>

            <GridLayoutItem col={3} row={2}>
              表示順
            </GridLayoutItem>
            <GridLayoutItem col={4} row={2}>
              <NumericTextBox
                value={
                  baseInfo.displayOrder === 0
                    ? undefined
                    : baseInfo.displayOrder
                }
                onChange={(e) => handleDisplayOrderVal(e)}
                spinners={false}
                disabled={isFixMode && isNotBuilderAuthority}
              />
            </GridLayoutItem>

            <GridLayoutItem col={5} row={2}>
              秘密保持契約
            </GridLayoutItem>
            <GridLayoutItem col={6} row={2}>
              <Switch
                size={'medium'}
                checked={baseInfo.ndaFlg === 1}
                onChange={handleNdaFlg}
                disabled={isNotBuilderAuthority}
              />
            </GridLayoutItem>

            <GridLayoutItem col={7} row={1}>
              物件情報開示
            </GridLayoutItem>
            <GridLayoutItem className="grid-layout-item-check" col={8} row={1}>
              <Switch
                size={'medium'}
                checked={baseInfo.buildingDiscloseFlg === 1}
                onChange={onSwitchBuildingDiscloseFlg}
                disabled={isNotBuilderAuthority}
              />
            </GridLayoutItem>

            <GridLayoutItem col={7} row={2}>
              入場可能人数
            </GridLayoutItem>
            <GridLayoutItem className="grid-layout-item-check" col={8} row={2}>
              <Switch
                size={'medium'}
                checked={baseInfo.ourCompanyCapacityFlg === 1}
                onChange={onSwitchOurCompanyCapacityFlg}
                disabled={isFixMode && isNotBuilderAuthority}
              />
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
      </Card>

      <div className="grid-container">
        <div className="grid-template">
          <Grid
            data={expenseList}
            editField="inEdit"
            style={{ height: '193px' }}
            onItemChange={handleDataExpense}
          >
            <GridToolbar>
              費目
              {!(isFixMode && isNotBuilderAuthority) &&
                <Button
                  className="button-add-new"
                  themeColor="primary"
                  onClick={addNewExpenseItem}
                >
                  行追加
                </Button>
            }
            </GridToolbar>
            <GridColumn
              title="削除"
              width={'45px'}
              field="isDel"
              cell={(props: GridCellProps) => (
                <Checkbox
                  className="column-del-flg"
                  disabled={isFixMode && isNotBuilderAuthority}
                  checked={props.dataItem.isDel}
                  onChange={() => (onChangeExpenseDelFlg(props.dataIndex, props.dataItem.isDel))}
                />
              )}
            />
            <GridColumn
              title="費目"
              cell={(props: GridCellProps) => (
                <DropDownCommandCell
                  handleOnChange={(value) => {
                    setExpenseList(
                      produce((draft) => {
                        draft[props.dataIndex].expense = value
                      })
                    )
                  }}
                  dropDownVal={props.dataItem.expense}
                  disabled={isFixMode && isNotBuilderAuthority}
                  />
              )}
              field="expense"
            />
            <GridColumn
              title="倉友会"
              field="soyukaiFlg"
              editor="boolean"
              cell={(props: GridCellProps) => (
                <td>
                  <Checkbox
                    className="checkbox-flg"
                    disabled={isFixMode && isNotBuilderAuthority}
                    checked={props.dataItem.soyukaiFlg}
                    onChange={() => (onChangeSoyukaiFlg(props.dataIndex, props.dataItem.soyukaiFlg === 1))}
                />
                </td>
              )}
            />
            <GridColumn
              title="ヒアリングシート"
              field="hearingSheetFlg"
              editor="boolean"
              cell={(props: GridCellProps) => (
                <td>
                  <Checkbox
                  className="checkbox-flg"
                  disabled={isFixMode && isNotBuilderAuthority}
                  checked={props.dataItem.hearingSheetFlg}
                  onChange={() => (onChangeHearingSheetFlg(props.dataIndex, props.dataItem.hearingSheetFlg === 1))}
                />
                </td>
              )}
            />
            <GridColumn
              title="回答依頼"
              field="replyReqFlg"
              editor="boolean"
              cell={(props: GridCellProps) => (
                <td>
                  <Checkbox
                    className="checkbox-flg"
                    disabled={isFixMode && isNotBuilderAuthority}
                    checked={props.dataItem.replyReqFlg}
                    onChange={() => (onChangeReplyReqFlg(props.dataIndex, props.dataItem.replyReqFlg === 1))}
                  />
                </td>
              )}
            />
          </Grid>
        </div>
      </div>

      <div className="grid-container">
        <div className="grid-template">
          <Grid
            data={factoryList}
            editField="inEdit"
            style={{ height: '193px' }}
            onItemChange={handleDataFactory}
          >
            <GridToolbar>
              工場
              {!(isFixMode && isNotBuilderAuthority) &&
                <Button
                  className="button-add-new"
                  themeColor="primary"
                  onClick={addNewFactoryItem}
                >
                  行追加
                </Button>
            }
            </GridToolbar>
            <GridColumn
              title="削除"
              width={'45px'}
              field="isDel"
              cell={(props: GridCellProps) => (
                <Checkbox
                  className="column-del-flg"
                  disabled={isFixMode && isNotBuilderAuthority}
                  checked={props.dataItem.isDel}
                  onChange={() => (onChangeFactoryDelFlg(props.dataIndex, props.dataItem.isDel))}
                />
              )}
            />
            <GridColumn
              title="費目"
              cell={(props: GridCellProps) => (
                <DropDownCommandCell
                  handleOnChange={(value) => {
                    setFactoryList(
                      produce((draft) => {
                        draft[props.dataIndex].expense = value
                      })
                    )
                  }}
                  dropDownVal={props.dataItem.expense}
                  disabled={(isFixMode && isNotBuilderAuthority) || props.dataItem.newAddFlg !== true}
                  />
              )}
              field="expense"
            />
            <GridColumn
              title="工場名"
              field="factoryNm"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
            <GridColumn
              title="配分比率(%)"
              field="allocationRatio"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
          </Grid>
        </div>
      </div>

      <div className="grid-container">
        <div className="grid-template">
          <Grid
            data={personList}
            editField="inEdit"
            style={{ height: '193px' }}
            onItemChange={handlePerson}
          >
            <GridToolbar>
              担当者
              {!(isFixMode && isNotBuilderAuthority) &&
                <Button
                  className="button-add-new"
                  themeColor="primary"
                  onClick={addNewPersonItem}
                >
                  行追加
                </Button>
            }
            </GridToolbar>
            <GridColumn
              title="削除"
              width={'45px'}
              field="isDel"
              cell={(props: GridCellProps) => (
                <Checkbox
                  className="column-del-flg"
                  disabled={isFixMode && isNotBuilderAuthority}
                  checked={props.dataItem.isDel}
                  onChange={() => (onChangePersonDelFlg(props.dataIndex, props.dataItem.isDel))}
                />
              )}
            />
            <GridColumn
              title="業者担当部署"
              field="supplierSection"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
            <GridColumn
              title="業者担当者"
              field="supplierPerson"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
            <GridColumn
              title="所属連絡先"
              field="affiliationContacts"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
            <GridColumn
              title="メールアドレス"
              field="mailAddress"
              editable={!(isFixMode && isNotBuilderAuthority)}
            />
            <GridColumn
              title="主担当者フラグ"
              field="mainPersonFlg"
              cell={(props: GridCellProps) => (
                <td>
                  <Checkbox
                    className="checkbox-flg"
                    disabled={isFixMode && isNotBuilderAuthority}
                    checked={props.dataItem.mainPersonFlg}
                    onChange={() => (onChangeMainPersonFlg(props.dataIndex, props.dataItem.mainPersonFlg === 1))}
                  />
                </td>
              )}
            />
          </Grid>
        </div>
      </div>

      <StackLayout gap={5} align={{ horizontal: 'end' }}>
        <Button className="pcy-button-long" onClick={goBack}>
          戻る
        </Button>
        {!(isFixMode && isNotBuilderAuthority) &&
          <Button
            themeColor="primary"
            className="pcy-button-long"
            onClick={handleLoginAmendment}
            disabled={isLoginUpdataDisabled || isNotBuilderAuthority}
          >
            登録/修正
          </Button>
        }
        {(isFixMode && !isNotBuilderAuthority) &&
          <Button
            themeColor="primary"
            className="pcy-button-long"
            onClick={handleDetailDelete}
            disabled={isLoginUpdataDisabled}
          >
            明細削除
          </Button>
        }

      </StackLayout>
      <ConfirmDialog
        title="戻る"
        open={confirmDialogGoBackShow}
        renderCloseButton
        id={'I003'}
        args={['戻る']}
        onClickOk={dialogConfirmGoBackBtn}
        onClickCancel={() => setConfirmDialogGoBackShow(false)}
      />
      <ConfirmDialog
        title={isFixMode ? '修正' : '登録'}
        open={confirmDialogSignInShow}
        renderCloseButton
        id={'I007'}
        args={isFixMode ? ['修正'] : ['登録']}
        onClickOk={dialogConfirmSignInBtn}
        onClickCancel={() => setConfirmDialogSignInShow(false)}
      />
      <ConfirmDialog
        title={'明細削除'}
        open={confirmDialogDetailDeleteShow}
        renderCloseButton
        id={'I007'}
        args={['明細削除']}
        onClickOk={dialogConfirmDetailDeleteBtn}
        onClickCancel={() => setConfirmDialogDetailDeleteShow(false)}
      />
    </>
  )
}
